import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useQuery } from 'urql';
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import PropTypes from 'prop-types';
import { cleanupData } from './utils';
import { CollectionComponent } from './CollectionComponent';
import { CollectionContext } from './context';
import { CollectionPaginator } from './CollectionPaginator';
import { getCollections, getCollectionSansSwatches } from './queries';
import { OV_ALERT, TEXT } from '../../config/constants';
import categoryFilter from './filters/category';
import CollectionLoading from '../../components/CollectionLoading';
import CollectionSet from '../../components/CollectionSet';
import css from './Collection.module.scss';
import ErrorMessage from '../../components/ErrorMessage';
import ExpandCollapser from '../../components/ExpandCollapser';
import fabricFilter from './filters/fabric';
import genderFilter from './filters/gender';
import Hero from '../../components/Hero';
import lengthFilter from './filters/length';
import paletteFilter from './filters/palette';
import priceFilter from './filters/price';
import RecommendationsAI from '../../components/RecommendationsAI';
import sizeFilter from './filters/size';
import supportFilter from './filters/support';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var filtersMap = {
  category: categoryFilter,
  fabric: fabricFilter,
  gender: genderFilter,
  length: lengthFilter,
  palette: paletteFilter,
  price: priceFilter,
  size: sizeFilter,
  support: supportFilter
};
export var Wrap = function Wrap(_ref) {
  var banner = _ref.banner,
      children = _ref.children,
      gender = _ref.gender,
      pageTitle = _ref.pageTitle;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [banner && /*#__PURE__*/_jsx(Hero, _objectSpread(_objectSpread({}, banner), {}, {
      gender: gender,
      pageTitle: pageTitle
    })), children && children]
  });
};
Wrap.defaultProps = {
  banner: false,
  children: false,
  gender: false,
  pageTitle: false
};
Wrap.propTypes = {
  banner: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape()]),
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.node]),
  gender: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  pageTitle: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
};

var CollectionLoader = function CollectionLoader(_ref2) {
  var banner = _ref2.banner,
      displaySwatches = _ref2.displaySwatches,
      displayEditorial = _ref2.displayEditorial,
      paginate = _ref2.paginate,
      paletteParam = _ref2.paletteParam,
      shopifyId = _ref2.shopifyId;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      gender = _useState2[0],
      setGender = _useState2[1];

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      productSets = _useState4[0],
      setProductSets = _useState4[1];

  var _useQuery = useQuery({
    query: displaySwatches ? getCollections : getCollectionSansSwatches,
    variables: {
      shopifyId: shopifyId,
      first: paginate ? 6 : undefined
    }
  }),
      _useQuery2 = _slicedToArray(_useQuery, 1),
      _useQuery2$ = _useQuery2[0],
      data = _useQuery2$.data,
      fetching = _useQuery2$.fetching,
      error = _useQuery2$.error; // console.info('collection', data, fetching, error);
  // set the product sets on the initial load of data


  useEffect(function () {
    var _data$collection, _data$collection2;

    if (fetching || error || !(data !== null && data !== void 0 && data.collection) || !(data !== null && data !== void 0 && (_data$collection = data.collection) !== null && _data$collection !== void 0 && _data$collection.productSets)) {
      return;
    }

    var sets = cleanupData(data === null || data === void 0 ? void 0 : (_data$collection2 = data.collection) === null || _data$collection2 === void 0 ? void 0 : _data$collection2.productSets, displaySwatches, displayEditorial);
    setProductSets(sets);
  }, [data === null || data === void 0 ? void 0 : data.collection, displayEditorial, displaySwatches, error, fetching]); // return the loader

  if (fetching) {
    return /*#__PURE__*/_jsx(Wrap, {
      banner: banner,
      children: /*#__PURE__*/_jsx(CollectionLoading, {})
    });
  }

  if (error) {
    var event = new CustomEvent(OV_ALERT, {
      detail: TEXT.alert
    });
    var _window = window,
        dispatchEvent = _window.dispatchEvent;
    var graphQLErrors = error.graphQLErrors,
        networkError = error.networkError;
    dispatchEvent(event);
    console.error('Collection error:', networkError, graphQLErrors);

    if (Sentry) {
      Sentry.captureException(error);
    }

    return /*#__PURE__*/_jsx(Wrap, {
      banner: banner,
      children: /*#__PURE__*/_jsx(ErrorMessage, {
        message: "There was an error loading this collection. Please refresh and try again.",
        details: error
      })
    });
  }

  if (!data.collection) {
    window.location.assign('/404');
  }

  var collection = data.collection;
  var paletteId = parseInt(paletteParam, 10);
  var id = collection.id,
      properties = collection.properties,
      title = collection.title;
  var bottomComponent = properties.bottomComponent,
      fabrics = properties.breadcrumbDisplayType,
      collectionDescription = properties.collectionDescription,
      filters = properties.filters,
      footerComponent = properties.footerComponent,
      headerComponent = properties.headerComponent,
      recommendation = properties.recommendation;

  _toConsumableArray(document.querySelectorAll('.nav-collection-id')).forEach(function (link) {
    link.href = "".concat(link.getAttribute('data-url')).concat(collection.id);
  });

  var urlParams = new URLSearchParams(window.location.search);
  var filty = JSON.parse(filters);
  var initialFilters = filty.filters.map(function (_ref3) {
    var name = _ref3.name,
        type = _ref3.type,
        values = _ref3.values;
    return filtersMap[type]({
      label: name,
      values: values,
      urlParams: urlParams,
      paletteId: paletteId
    });
  });
  var drawerSection = {
    child: /*#__PURE__*/_jsx("div", {
      children: collectionDescription
    }),
    className: css.drawer,
    id: "".concat(id, "-description"),
    onClick: function onClick() {},
    title: 'Collection Details'
  };
  var collectionProps = {
    collection: collection,
    productSets: productSets,
    initialFilters: initialFilters
  };
  return /*#__PURE__*/_jsx(CollectionContext.Provider, {
    value: {
      fabrics: fabrics,
      gender: gender,
      setGender: setGender
    },
    children: /*#__PURE__*/_jsxs(Wrap, {
      banner: properties.banner,
      gender: gender,
      pageTitle: title,
      children: [headerComponent && /*#__PURE__*/_jsx(CollectionComponent, {
        component: headerComponent,
        collectionId: collection.id
      }), productSets.length === 1 && /*#__PURE__*/_jsx(CollectionPaginator, _objectSpread(_objectSpread({}, collectionProps), {}, {
        after: productSets[0].products.pageInfo.endCursor,
        displayEditorial: displayEditorial,
        displaySwatches: displaySwatches,
        collectionProductSetId: productSets[0].id,
        paginating: productSets[0].products.pageInfo.hasNextPage
      })), productSets.length !== 1 && /*#__PURE__*/_jsx(CollectionSet, _objectSpread({}, collectionProps)), bottomComponent && /*#__PURE__*/_jsx(CollectionComponent, {
        collectionId: collection.id,
        component: bottomComponent
      }), footerComponent && /*#__PURE__*/_jsx(CollectionComponent, {
        collectionId: collection.id,
        component: footerComponent
      }), recommendation && /*#__PURE__*/_jsx(RecommendationsAI, _objectSpread({}, recommendation)), collectionDescription && /*#__PURE__*/_jsx("div", {
        className: css.expanderRoot,
        children: /*#__PURE__*/_jsx("div", {
          className: css.expander,
          children: /*#__PURE__*/_jsx(ExpandCollapser, {
            alignLeft: true,
            border: false,
            index: 0,
            sections: [drawerSection]
          })
        })
      })]
    })
  });
};

CollectionLoader.defaultProps = {
  banner: {
    component: 'TEXT_ONLY'
  },
  paletteParam: '',
  shopifyId: ''
};
CollectionLoader.propTypes = {
  banner: PropTypes.shape(),
  displaySwatches: PropTypes.bool.isRequired,
  displayEditorial: PropTypes.bool.isRequired,
  paginate: PropTypes.bool,
  paletteParam: PropTypes.string,
  shopifyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
export default CollectionLoader;